// API Configuration
const SERVER_CONFIGS = {
  ENV: process.env.REACT_APP_ENV || 'prod',
  TRANSLATE_LOCAL_SERVER:
    process.env.REACT_APP_TRANSLATE_LOCAL_SERVER || 'http://localhost:6500',
  TRANSLATE_PROD_SERVER:
    process.env.REACT_APP_TRANSLATE_PROD_SERVER ||
    'https://www.translate.paralegal.lk/api',
};

const ENV = SERVER_CONFIGS.ENV;
const HOST =
  ENV === 'local'
    ? SERVER_CONFIGS.TRANSLATE_LOCAL_SERVER
    : SERVER_CONFIGS.TRANSLATE_PROD_SERVER;

// Rate-limited request function
const makeRequest = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 15000);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
        ...options.headers,
      },
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// Translation Services
export const translationService = {
  // Upload file
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await makeRequest(`${HOST}/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  },

  // Get translation rate
  getTranslationRate: async (docPath, sourceToTarget, translationType) => {
    const response = await makeRequest(`${HOST}/rate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        doc_path: docPath,
        source_to_target: sourceToTarget,
        translation_type: translationType,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get translation rate');
    }

    return response.json();
  },

  // Start translation
  startTranslation: async (docPath, sourceToTarget) => {
    const response = await makeRequest(`${HOST}/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        doc_path: docPath,
        source_to_target: sourceToTarget,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to start translation');
    }

    return response.json();
  },

  // Get task status
  getTaskStatus: async (taskId) => {
    const response = await makeRequest(`${HOST}/task/${taskId}`);

    if (!response.ok) {
      throw new Error(
        `Failed to get task status: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Get recent translations
  getRecentTranslations: async () => {
    const response = await makeRequest(`${HOST}/recent-translations`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Recent translations endpoint not available');
      }
      throw new Error(
        `Failed to fetch recent translations: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  },

  // Retry failed translation
  retryTranslation: async (taskId) => {
    const response = await makeRequest(`${HOST}/retry-translation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ task_id: taskId }),
    });

    if (!response.ok) {
      throw new Error('Failed to retry translation');
    }

    return response.json();
  },

  // Get download URL
  getDownloadUrl: (filePath) => {
    return `${HOST}/download?file=${encodeURIComponent(filePath)}`;
  },
};

export default translationService;
